/** Projections over comments: threads and anchors. */

import { plainProjectEntry } from '../project/scalars'
import type { CommentEntry, CommentTarget, Mention } from './types'

/**
 * Project a project doc's comments (RFC 0267 &sect;4a), newest-first, optionally
 * filtered to a `target`. What `mod.comments.list(target)` and the agent's
 * comment tools resolve to. The filter is a SUBSET match: a comment is included
 * when every field present in the query target equals the comment's target, so
 * `{ documentId }` returns every comment on that document and `{ sessionId }`
 * returns every comment anywhere in that session (whole-session and per-message).
 * An empty/absent query returns all comments in the project.
 */
/** The mentions on one comment, as plain data. Anything malformed is dropped. */
function readMentions(raw: unknown): Mention[] {
  if (!Array.isArray(raw)) return []
  const out: Mention[] = []
  for (const m of raw) {
    if (!m || typeof m !== 'object') continue
    const kind = (m as { kind?: unknown }).kind
    const id = (m as { id?: unknown }).id
    if (kind !== 'session' && kind !== 'document') continue
    if (typeof id !== 'string' || !id) continue
    const at = Number((m as { at?: unknown }).at)
    const len = Number((m as { len?: unknown }).len)
    out.push({
      kind,
      id,
      at: Number.isFinite(at) ? at : 0,
      len: Number.isFinite(len) ? len : 0,
    })
  }
  return out
}
/**
 * A stored string, however it happens to be stored.
 *
 * An id or an author on the index is written as an Automerge `ImmutableString`
 * so it costs one op instead of one per character (see `imm` in `store.ts`).
 * The wrapper is not a JS string: `typeof v === 'string'` is false for it, so
 * a plain type test reads a field that IS there as absent, and the caller gets
 * an empty author or a reply with no parent. That is a silent wrong answer
 * rather than a crash, which is why every read of one of these fields goes
 * through here.
 */
function str(v: unknown): string {
  if (typeof v === 'string') return v
  if (v == null) return ''
  const inner = (v as { val?: unknown }).val
  return typeof inner === 'string' ? inner : ''
}

export function projectComments(
  projectDoc: unknown,
  target?: Record<string, string | undefined>,
): CommentEntry[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const map = (projectDoc as { comments?: Record<string, unknown> }).comments
  if (!map || typeof map !== 'object') return []
  // Only the fields the caller actually set constrain the match; undefined
  // fields in the query are ignored so `{ documentId: x }` doesn't demand the
  // comment ALSO have an undefined sessionId.
  const constraints = Object.entries(target ?? {}).filter(
    ([, v]) => v !== undefined,
  )
  const out: CommentEntry[] = []
  for (const raw of Object.values(map)) {
    if (!raw || typeof raw !== 'object') continue
    // Plain strings out of a scalar entry (RFC 0357 S11), so the target
    // compare and the typeof checks below see what they were written for.
    const c = plainProjectEntry(raw) as CommentEntry
    const t = (c.target ?? {}) as Record<string, unknown>
    if (!constraints.every(([k, v]) => t[k] === v)) continue
    const mentions = readMentions(c.mentions)
    out.push({
      id: String(c.id ?? ''),
      target: { ...(c.target ?? {}) },
      author: str(c.author),
      body: str(c.body),
      resolved: c.resolved === true,
      createdAt: typeof c.createdAt === 'number' ? c.createdAt : 0,
      updatedAt: typeof c.updatedAt === 'number' ? c.updatedAt : 0,
      // Only present on a reply (RFC 0289 §3). Spread rather than assigned so a
      // root's target-shaped equality checks keep working and no entry ever
      // carries `parentId: undefined`.
      ...(str(c.parentId) ? { parentId: str(c.parentId) } : {}),
      // What the body names (RFC 0292). Rebuilt scalar by scalar rather than
      // handed over as the Automerge array, so a caller can hold it after the
      // document moves on, and MALFORMED ENTRIES ARE DROPPED: a chip with no id
      // renders as a link to nothing, which reads as a bug in the person's text
      // rather than in ours.
      ...(mentions.length > 0 ? { mentions } : {}),
      // The session to wake when somebody answers this (RFC 0289), spread
      // for the same reason: absent on a person's comment and on every comment
      // written before the field existed.
      ...(str(c.authorSessionId)
        ? { authorSessionId: str(c.authorSessionId) }
        : {}),
      // Who read it and chose not to answer (RFC 0324 3g). Copied to a plain
      // object for the same reason as mentions.
      ...(readReadBy(c.readBy) ?? {}),
    })
  }
  return out.sort((a, b) => b.createdAt - a.createdAt)
}
function readReadBy(
  raw: unknown,
): { readBy: Record<string, number> } | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const out: Record<string, number> = {}
  for (const [id, at] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof at === 'number') out[id] = at
  }
  return Object.keys(out).length > 0 ? { readBy: out } : undefined
}

/**
 * A thread (RFC 0289 &sect;4): one root comment and the answers to it.
 *
 * A PROJECTION, not a record. Nothing about a thread is stored; it is `parentId`
 * read back as a grouping, so there is no thread object to keep in step with the
 * comments it contains.
 */
export interface CommentThread {
  root: CommentEntry
  /** Answers, oldest first: a conversation reads down. */
  replies: CommentEntry[]
}
/**
 * Group a project doc's comments into threads (RFC 0289 &sect;4), filtered by
 * the same subset match as `projectComments`.
 *
 * Roots order unresolved-first then newest (the side column's existing rule);
 * replies order oldest-first within a thread.
 *
 * A reply whose parent is absent from the FILTERED set is PROMOTED to a root
 * rather than dropped. A reply can outrun its root across a sync boundary, and a
 * reader that discards any comment whose parent it has not seen loses text that
 * was written. Promotion is visibly wrong for a moment and self-corrects when
 * the root arrives; dropping is invisible and permanent.
 */
export function projectThreads(
  projectDoc: unknown,
  target?: Record<string, string | undefined>,
): CommentThread[] {
  const comments = projectComments(projectDoc, target)
  const byId = new Map(comments.map((c) => [c.id, c]))

  // The root a comment belongs to. `parentId` is one level deep by construction
  // (replyToComment normalises it), but a doc written by an older or buggier
  // client can carry a chain, so walk with a visited guard rather than trusting
  // the invariant: a cycle here would hang the side column.
  const rootIdOf = (c: CommentEntry): string => {
    let cur = c
    const seen = new Set<string>([cur.id])
    while (cur.parentId) {
      const parent = byId.get(cur.parentId)
      // Parent outside the filtered set: promote this comment (see above).
      if (!parent) break
      // A CYCLE, so no member of it can be reached from a genuine root. Making
      // the comment its own root is the only answer that keeps every one of
      // them: stopping the walk here instead would resolve `a` to `b` and `b`
      // to `a`, leaving a thread list with no roots at all and both comments
      // silently gone, which is the loss the promotion rule exists to prevent.
      if (seen.has(parent.id)) return c.id
      seen.add(parent.id)
      cur = parent
    }
    return cur.id
  }

  const roots: CommentEntry[] = []
  const repliesByRoot = new Map<string, CommentEntry[]>()
  for (const c of comments) {
    const rootId = rootIdOf(c)
    if (rootId === c.id) {
      roots.push(c)
      continue
    }
    const list = repliesByRoot.get(rootId)
    if (list) list.push(c)
    else repliesByRoot.set(rootId, [c])
  }

  return roots
    .sort((a, b) => {
      if (a.resolved !== b.resolved) return a.resolved ? 1 : -1
      return b.createdAt - a.createdAt
    })
    .map((root) => ({
      root,
      replies: (repliesByRoot.get(root.id) ?? []).sort(
        (a, b) => a.createdAt - b.createdAt,
      ),
    }))
}
/**
 * The ANCHOR a comment is about (RFC 0289 &sect;7): the most specific positional
 * fields its target carries, by a fixed precedence.
 *
 * `commit` is NEVER part of the anchor. It records which revision a comment was
 * written against (RFC 0285 S6), so including it would split a conversation
 * precisely when the branch moved, which is when the conversation matters most.
 * `anchorText` is in the key only where nothing more specific exists: for a
 * document comment it IS the anchor, while for a hunk comment it is only the
 * witness that the line changed.
 */
export function commentAnchor(
  target: CommentTarget | undefined,
): CommentTarget {
  const t = target ?? {}
  const pick = (...keys: (keyof CommentTarget)[]): CommentTarget => {
    const out: CommentTarget = {}
    for (const k of keys) {
      const v = t[k]
      if (typeof v === 'string' && v) out[k] = v
    }
    return out
  }
  if (t.hunk) return pick('branch', 'path', 'hunk')
  if (t.messageId) return pick('sessionId', 'messageId')
  // A node BEFORE the text that labels it, for the reason `anchorText` is last
  // among the document keys: the id is stable and the label is not, so grouping
  // on the label would split a node's thread the moment somebody renamed it.
  if (t.nodeId) return pick('documentId', 'nodeId')
  if (t.path) return pick('branch', 'path')
  if (t.anchorText) return pick('documentId', 'anchorText')
  if (t.documentId) return pick('documentId')
  if (t.sessionId) return pick('sessionId')
  return {}
}
/** Every thread written against one anchor (RFC 0289 &sect;7). */
export interface CommentGroup {
  /** What these threads are about. */
  anchor: CommentTarget
  threads: CommentThread[]
}
/**
 * Group threads by what they are anchored to (RFC 0289 &sect;7).
 *
 * Threading only links comments where somebody pressed Reply. Two people who
 * commented on the same line independently have no `parentId` between them and
 * never will, so the column's unit is the ANCHOR: one block per thing being
 * talked about, holding every thread written against it.
 *
 * ORDER IS THE SURFACE'S CALL, so this preserves the order it was handed: a
 * group appears where its first thread appeared, and threads keep their relative
 * order within it. The review app sorts by file and diff position before calling
 * this; the side column hands over `projectThreads` order (unresolved first,
 * then newest) and gets that back.
 */
export function groupThreadsByAnchor(threads: CommentThread[]): CommentGroup[] {
  const groups: CommentGroup[] = []
  const byKey = new Map<string, CommentGroup>()
  for (const thread of threads) {
    const anchor = commentAnchor(thread.root.target)
    // A stable key: entries sorted so field order in the stored target cannot
    // split one anchor into two groups.
    const key = JSON.stringify(
      Object.entries(anchor).sort(([a], [b]) => (a < b ? -1 : 1)),
    )
    const existing = byKey.get(key)
    if (existing) {
      existing.threads.push(thread)
      continue
    }
    const group: CommentGroup = { anchor, threads: [thread] }
    byKey.set(key, group)
    groups.push(group)
  }
  return groups
}
